import { getBrowserApiBaseUrl } from "@/config/env";
import {
  buildStreamFileUrl,
  ensureFileToken,
  generateFilePreviewUrlWithToken,
  isImageFile,
  openPreviewTarget,
} from "@/lib/utils/filePreview";
import { showError } from "@/lib/utils/notifications";
import type { IncomeProofFile } from "../../types/tabs";

const getIncomePreviewApiBaseUrl = (): string =>
  getBrowserApiBaseUrl().replace(/\/+$/, "");

const normalizeServeFilePreviewUrl = (rawUrl: string): string | null => {
  try {
    const normalizedUrl =
      rawUrl.startsWith("/") || /^https?:\/\//i.test(rawUrl) ? rawUrl : `/${rawUrl}`;
    const url = new URL(normalizedUrl, window.location.origin);
    const apiBaseUrl = getIncomePreviewApiBaseUrl();
    return `${apiBaseUrl}${url.pathname}${url.search}`;
  } catch {
    return null;
  }
};

const normalizePreviewFilePath = (rawPath: string): string | null => {
  let filePath = rawPath.trim();
  if (!filePath) return null;

  if (filePath.includes("/file-handlers")) {
    try {
      const nestedUrl = new URL(filePath, window.location.origin);
      const innerPath = nestedUrl.searchParams.get("path");
      if (innerPath) filePath = decodeURIComponent(innerPath);
    } catch {
      const match = filePath.match(/[?&]path=([^&]+)/);
      if (match) filePath = decodeURIComponent(match[1]);
    }
  }

  if (filePath.includes("/serve-file-preview/") || filePath.startsWith("serve-file-preview/")) {
    return normalizeServeFilePreviewUrl(filePath);
  }

  if (filePath.includes("/stream-file?path=")) {
    try {
      const streamUrl = new URL(filePath);
      const pathParam = streamUrl.searchParams.get("path");
      if (pathParam) filePath = decodeURIComponent(pathParam);
    } catch {
      const match = filePath.match(/[?&]path=([^&]+)/);
      if (match) filePath = decodeURIComponent(match[1]);
    }
  } else if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    try {
      filePath = new URL(filePath).pathname.replace(/^\//, "");
    } catch {
      return null;
    }
  }

  if (filePath.includes("/serve-file-preview/") || filePath.startsWith("serve-file-preview/")) {
    return normalizeServeFilePreviewUrl(filePath);
  }

  return filePath.replace(/^\//, "");
};

export async function previewApplicationIncomeFile(
  applicantId: string | number,
  file: IncomeProofFile,
  allFiles: IncomeProofFile[],
): Promise<void> {
  const fileName = file.filename || file.name || file.filepath || "document";

  if (file.attachment_id) {
    const token = await ensureFileToken();
    if (!token || token.trim() === "") {
      showError("Unable to open file preview. Please try again in a moment.");
      return;
    }

    const secureUrl = generateFilePreviewUrlWithToken(
      file.attachment_id,
      "PROOF_OF_INCOME",
      applicantId,
      "APPLICATION",
      token,
    );
    const imageFilesWithIds = allFiles.filter(
      (f): f is IncomeProofFile & { attachment_id: string | number } =>
        f.attachment_id != null &&
        isImageFile(f.filename || f.name || f.filepath || ""),
    );
    const filesList =
      imageFilesWithIds.length > 1
        ? imageFilesWithIds.map((f) =>
            generateFilePreviewUrlWithToken(
              f.attachment_id,
              "PROOF_OF_INCOME",
              applicantId,
              "APPLICATION",
              token,
            ),
          )
        : undefined;

    openPreviewTarget(secureUrl, fileName, filesList);
    return;
  }

  if (!file.filepath) return;
  const normalizedPath = normalizePreviewFilePath(file.filepath);
  if (!normalizedPath) return;

  if (normalizedPath.includes("/serve-file-preview/")) {
    openPreviewTarget(normalizedPath, fileName);
    return;
  }

  const previewUrl = buildStreamFileUrl(normalizedPath);
  const imageFilesWithoutIds = allFiles.filter(
    (f) =>
      !f.attachment_id &&
      f.filepath &&
      isImageFile(f.filename || f.name || f.filepath || ""),
  );
  const filesList = imageFilesWithoutIds
    .map((f) => {
      const nextPath = normalizePreviewFilePath(String(f.filepath || ""));
      if (!nextPath || nextPath.includes("/serve-file-preview/")) return null;
      return buildStreamFileUrl(nextPath);
    })
    .filter((url): url is string => url !== null);

  openPreviewTarget(previewUrl, fileName, filesList.length > 1 ? filesList : undefined);
}
